import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { Logo } from '../components/Logo';
import { IMAGES } from '../data/products';
import { useScreenInit } from '../useScreenInit.js';
type Tab = 'login' | 'register';
const fieldClass = 'w-full border border-white/10 bg-ink-800 px-4 py-3.5 text-sm text-white placeholder:text-white/30 focus:border-pink-500 focus:outline-none';
const labelClass = 'block text-[10px] font-bold uppercase tracking-[0.2em] text-white/45';
function GoogleIcon({
  className = ''
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" aria-hidden focusable="false" data-id="element-1099" data-mp="2tnywoctfwurj">
      <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.54 5.54 0 0 1-2.4 3.63v3h3.86c2.26-2.09 3.56-5.17 3.56-8.87z" data-id="element-1100" data-mp="2ym0mrhpa9uci" />
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z" data-id="element-1101" data-mp="3r463fd7groul" />
      <path fill="#FBBC05" d="M5.27 14.29a7.2 7.2 0 0 1 0-4.58V6.62H1.29a11.99 11.99 0 0 0 0 10.76l3.98-3.09z" data-id="element-1102" data-mp="2mi7s87cubaru" />
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.7 0 3.99 2.47 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" data-id="element-1103" data-mp="317nfveh2r03b" />
    </svg>;
}
function strengthOf(value: string): {
  score: number;
  label: string;
  color: string;
} {
  let score = 0;
  if (value.length >= 8) score += 1;
  if (/[A-Z]/.test(value)) score += 1;
  if (/[0-9]/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;
  if (!value) return {
    score: 0,
    label: '',
    color: 'bg-ink-500'
  };
  if (score <= 1) return {
    score: 1,
    label: 'Débil',
    color: 'bg-red-500'
  };
  if (score === 2) return {
    score: 2,
    label: 'Aceptable',
    color: 'bg-amber-500'
  };
  if (score === 3) return {
    score: 3,
    label: 'Buena',
    color: 'bg-teal-500'
  };
  return {
    score: 4,
    label: 'Excelente',
    color: 'bg-pink-500'
  };
}
export function Login() {
  const screenInit = useScreenInit();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>(screenInit.tab as Tab ?? 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [terms, setTerms] = useState(false);
  const [news, setNews] = useState(true);
  const [remember, setRemember] = useState(true);
  const strength = useMemo(() => strengthOf(password), [password]);
  const tabs: {
    id: Tab;
    label: string;
  }[] = [{
    id: 'login',
    label: 'Iniciar sesión'
  }, {
    id: 'register',
    label: 'Crear cuenta'
  }];
  return <div className="relative flex min-h-screen w-full items-center justify-center bg-black px-5 py-14" data-id="element-1104" data-mp="3768r9eu2lwfe">
      <img src={IMAGES.about} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-20" data-id="element-1105" data-mp="3c40gg13bz4fw" />
      <div className="absolute inset-0 bg-black/80" data-id="element-1106" data-mp="15iw0ry2p54ki" />

      <div className="relative w-full max-w-md" data-id="element-1107" data-mp="15fi0cvdlf5fp">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/45 transition-colors hover:text-white" data-id="element-1108" data-mp="10hqa05sljrdm">
          <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-1109" data-mp="qln5wue6pio1" />
          Volver a la tienda
        </Link>

        <main className="border border-white/10 bg-ink-800 p-7 sm:p-9" data-id="element-1110" data-mp="1o7apuftge8io">
          <div className="flex justify-center" data-id="element-1111" data-mp="1j9j27xzud8ka">
            <Logo size="md" data-id="element-1112" data-mp="1j9rdjlgbg5td" />
          </div>
          <p className="mt-4 text-center text-sm text-white/45" data-id="element-1113" data-mp="1ei9c6wads37v">
            {tab === 'login' ? 'Ingresá para ver tus pedidos y comprar más rápido.' : 'Creá tu cuenta y entrená sin dolor con ZADAR.'}
          </p>

          {/* TABS */}
          <div role="tablist" aria-label="Acceso a la cuenta" className="mt-8 grid grid-cols-2 border-b border-white/10" data-id="element-1114" data-mp="270wszzicgw35">
            {tabs.map(item => {
            const active = tab === item.id;
            return <button key={item.id} type="button" role="tab" aria-selected={active} onClick={() => setTab(item.id)} className={`relative pb-3.5 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors ${active ? 'text-white' : 'text-white/40 hover:text-white/70'}`} data-id="element-1115" data-mp="1rpyghdhopd95">
                  {item.label}
                  <span className={`absolute -bottom-[1px] left-0 h-[2px] w-full transition-colors ${active ? 'bg-pink-500' : 'bg-transparent'}`} data-id="element-1116" data-mp="1vrjzlgktanrs" />
                </button>;
          })}
          </div>

          {/* LOGIN */}
          {tab === 'login' && <form className="mt-8 space-y-5" onSubmit={e => {
          e.preventDefault();
          navigate('/mis-pedidos');
        }} aria-label="Iniciar sesión" data-id="element-1117" data-mp="2t80ndf1wkeoe">
              <div data-id="element-1118" data-mp="rh9y1012pvqt">
                <label className={labelClass} htmlFor="login-email" data-id="element-1119" data-mp="rfp3vbio8eth">
                  Email
                </label>
                <input id="login-email" type="email" required autoComplete="email" placeholder="tu@email.com" className={`mt-2 ${fieldClass}`} data-id="element-1120" data-mp="mip57lo5iece" />
              </div>

              <div data-id="element-1121" data-mp="7ukk6b1mlqqg">
                <label className={labelClass} htmlFor="login-password" data-id="element-1122" data-mp="7qwrfe4098cs">
                  Contraseña
                </label>
                <div className="relative mt-2" data-id="element-1123" data-mp="1j7whe32vrb3n">
                  <input id="login-password" type={showPassword ? 'text' : 'password'} required autoComplete="current-password" placeholder="••••••••" className={`${fieldClass} pr-12`} data-id="element-1124" data-mp="1j6ld825iw1c0" />
                  <button type="button" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'} className="absolute right-1 top-1/2 -translate-y-1/2 p-3 text-white/40 transition-colors hover:text-white" data-id="element-1125" data-mp="gwutcbv70dd8">
                    {showPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-1126" data-mp="25lx3jwsbtr4" /> : <EyeIcon className="h-4 w-4" data-id="element-1127" data-mp="6x416gu3uagb" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-4" data-id="element-1128" data-mp="36jkg75cb3bzo">
                <label className="flex cursor-pointer items-center gap-2.5 text-xs text-white/55" data-id="element-1129" data-mp="3akvwuld7cmvq">
                  <input type="checkbox" checked={remember} onChange={() => setRemember(v => !v)} className="h-4 w-4 shrink-0 appearance-none border border-white/25 bg-transparent checked:border-pink-500 checked:bg-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500" data-id="element-1130" data-mp="332elsapoh00n" />
                  Recordarme
                </label>
                <a href="#" className="text-xs text-pink-400 transition-colors hover:text-pink-500 hover:underline" data-id="element-1131" data-mp="2k4vb72nkn9c4">
                  ¿Olvidaste tu contraseña?
                </a>
              </div>

              <button type="submit" className="w-full bg-pink-500 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-pink-600" data-id="element-1132" data-mp="26cfbobtvptfy">
                Iniciar sesión
              </button>

              <div className="flex items-center gap-4" data-id="element-1133" data-mp="1wg29epk9q79l">
                <span className="h-[1px] flex-1 bg-white/10" data-id="element-1134" data-mp="3kbp9kw40tml3" />
                <span className="text-xs uppercase text-white/35" data-id="element-1135" data-mp="3p98l3o9c3l9c">o</span>
                <span className="h-[1px] flex-1 bg-white/10" data-id="element-1136" data-mp="3p7e94zg1j3he" />
              </div>

              <button type="button" onClick={() => navigate('/mis-pedidos')} className="flex w-full items-center justify-center gap-3 border border-white/20 py-3.5 text-xs font-semibold text-white transition-colors hover:border-white hover:bg-white/5" data-id="element-1137" data-mp="3b9g089khnz9a">
                <GoogleIcon className="h-[18px] w-[18px]" data-id="element-1138" data-mp="36ch7nvgh40d7" />
                Continuar con Google
              </button>

              <p className="pt-1 text-center text-xs text-white/45" data-id="element-1139" data-mp="2wkjtqgy2yuas">
                ¿No tenés cuenta?{' '}
                <button type="button" onClick={() => setTab('register')} className="font-semibold text-pink-400 transition-colors hover:text-pink-500 hover:underline" data-id="element-1140" data-mp="2ikiic7k6w4h2">
                  Crear cuenta
                </button>
              </p>
            </form>}

          {/* REGISTER */}
          {tab === 'register' && <form className="mt-8 space-y-5" onSubmit={e => {
          e.preventDefault();
          navigate('/mis-pedidos');
        }} aria-label="Crear cuenta" data-id="element-1141" data-mp="2dx2d6zopt7xk">
              <div className="grid gap-4 sm:grid-cols-2" data-id="element-1142" data-mp="2mvea1jg6d1yg">
                <div data-id="element-1143" data-mp="2hyw8stk1zlqv">
                  <label className={labelClass} htmlFor="reg-first" data-id="element-1144" data-mp="2hxctb5681eq5">
                    Nombre
                  </label>
                  <input id="reg-first" required autoComplete="given-name" placeholder="Camila" className={`mt-2 ${fieldClass}`} data-id="element-1145" data-mp="2wm8v4khopvib" />
                </div>
                <div data-id="element-1146" data-mp="31n52x955q1t0">
                  <label className={labelClass} htmlFor="reg-last" data-id="element-1147" data-mp="31nyb8ejrkzd9">
                    Apellido
                  </label>
                  <input id="reg-last" required autoComplete="family-name" placeholder="Rodríguez" className={`mt-2 ${fieldClass}`} data-id="element-1148" data-mp="p4xhhdq2pwne" />
                </div>
              </div>

              <div data-id="element-1149" data-mp="acdeqe6u5r6h">
                <label className={labelClass} htmlFor="reg-email" data-id="element-1150" data-mp="181q975jlepfm">
                  Email
                </label>
                <input id="reg-email" type="email" required autoComplete="email" placeholder="tu@email.com" className={`mt-2 ${fieldClass}`} data-id="element-1151" data-mp="132djmd1c749c" />
              </div>

              <div data-id="element-1152" data-mp="3itrswris31pm">
                <label className={labelClass} htmlFor="reg-password" data-id="element-1153" data-mp="3iu9sny4nfy2a">
                  Contraseña
                </label>
                <div className="relative mt-2" data-id="element-1154" data-mp="3eqv3agutm5c6">
                  <input id="reg-password" type={showNewPassword ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" placeholder="Mínimo 8 caracteres" aria-describedby="password-strength" className={`${fieldClass} pr-12`} data-id="element-1155" data-mp="1uanjhywdbkrv" />
                  <button type="button" onClick={() => setShowNewPassword(v => !v)} aria-label={showNewPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'} className="absolute right-1 top-1/2 -translate-y-1/2 p-3 text-white/40 transition-colors hover:text-white" data-id="element-1156" data-mp="2hzyc2t3ke8ux">
                    {showNewPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-1157" data-mp="1zx9f9m9k9v1h" /> : <EyeIcon className="h-4 w-4" data-id="element-1158" data-mp="1zzdrcrhm60cu" />}
                  </button>
                </div>
                <div className="mt-3 flex items-center gap-3" data-id="element-1159" data-mp="28xz9wmn35fdm">
                  <div className="flex flex-1 gap-1.5" aria-hidden data-id="element-1160" data-mp="28w4kow4s713q">
                    {[1, 2, 3, 4].map(step => <span key={step} className={`h-1 flex-1 transition-colors ${strength.score >= step ? strength.color : 'bg-ink-600'}`} data-id="element-1161" data-mp="2iwf08neagkm2" />)}
                  </div>
                  <span id="password-strength" className="w-20 text-right text-[10px] font-bold uppercase tracking-[0.14em] text-white/50" data-id="element-1162" data-mp="2nndihsiu7sry">
                    {strength.label || 'Seguridad'}
                  </span>
                </div>
              </div>

              <div data-id="element-1163" data-mp="3g9bvgm9wulym">
                <label className={labelClass} htmlFor="reg-confirm" data-id="element-1164" data-mp="3gawr6f7yoay2">
                  Repetir contraseña
                </label>
                <input id="reg-confirm" type="password" required autoComplete="new-password" placeholder="••••••••" className={`mt-2 ${fieldClass}`} data-id="element-1165" data-mp="3l6lsamzmpzwh" />
              </div>

              <div className="space-y-3 pt-1" data-id="element-1166" data-mp="2xh0x95q7bc6u">
                <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-white/55" data-id="element-1167" data-mp="32ac3p5r3pqy8">
                  <input type="checkbox" required checked={terms} onChange={() => setTerms(v => !v)} className="mt-0.5 h-4 w-4 shrink-0 appearance-none border border-white/25 bg-transparent checked:border-pink-500 checked:bg-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500" data-id="element-1168" data-mp="2nlz9oebkqi6d" />
                  <span data-id="element-1169" data-mp="2bhbr5w9r8jih">
                    Acepto los{' '}
                    <a href="#" className="text-pink-400 hover:text-pink-500 hover:underline" data-id="element-1170" data-mp="26jtt50x9zop9">
                      términos y condiciones
                    </a>{' '}
                    y la política de privacidad.
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-white/55" data-id="element-1171" data-mp="1st6oyt8qx6ls">
                  <input type="checkbox" checked={news} onChange={() => setNews(v => !v)} className="mt-0.5 h-4 w-4 shrink-0 appearance-none border border-white/25 bg-transparent checked:border-pink-500 checked:bg-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500" data-id="element-1172" data-mp="1nrhepftckwyr" />
                  <span data-id="element-1173" data-mp="389yuievfmfri">Quiero recibir ofertas y novedades por email.</span>
                </label>
              </div>

              <button type="submit" className="w-full bg-pink-500 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-pink-600" data-id="element-1174" data-mp="3gpemtrv666fu">
                Crear cuenta
              </button>

              <p className="pt-1 text-center text-xs text-white/45" data-id="element-1175" data-mp="3uhuha4a08w55">
                ¿Ya tenés cuenta?{' '}
                <button type="button" onClick={() => setTab('login')} className="font-semibold text-pink-400 transition-colors hover:text-pink-500 hover:underline" data-id="element-1176" data-mp="2xrtz4o455dwt">
                  Iniciar sesión
                </button>
              </p>
            </form>}
        </main>
      </div>
    </div>;
}